import { Link } from "@tanstack/react-router";
import { ArrowRight, Gift, Lightbulb, Sparkles } from "lucide-react";
import React, { useState } from "react";

import { LinkedInIcon } from "~/components/icons/linkedin-icon";
import { Button } from "~/components/ui/button";
import { Modal } from "~/components/ui/modal/modal";
import SignatureYuriUrl from "~/img/signature-yuri.png";
import { cn } from "~/lib/cn";

import { IcfPartnerBox } from "../icf-partner-box";

import { CDNImage } from "./cdn-image";

export interface PreGeneratedProfileBannerProps {
  coachName: string;
  fullName: string;
  username: string;
  profileId?: string;
}

/** Banner that tells a coach their profile was pre-generated and lets them opt out. */
export const PreGeneratedProfileBanner = ({
  coachName,
  fullName,
  profileId,
}: PreGeneratedProfileBannerProps) => {
  const [showOptOutModal, setShowOptOutModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const profileUrl = `/page/coach-complete-profile/${profileId ? `?id=${profileId}` : ""}`;

  return (
    <div
      className={cn(
        "relative mx-auto mb-6 w-full max-w-xl rounded-xl border border-purple-200 bg-purple-50/50 p-4 px-8 shadow shadow-lg shadow-purple-500/5",
      )}
    >
      <div className={cn("relative items-start overflow-hidden")}>
        <div className="flex flex-col items-center gap-4 text-center">
          <Gift className="mt-8 size-16 text-purple-700" />
          <div className="flex flex-col items-center gap-2">
            <h2 className="text-text-primary mx-8 font-heading text-3xl leading-tight font-medium tracking-tight">
              Hi {coachName},
            </h2>
            <p className="text-text-secondary">
              Dream Coach Match created this profile for you.
            </p>
            <p className="text-text-secondary">
              It&rsquo;s not on the marketplace until you approve it.
            </p>
          </div>
          <div className="my-4 flex flex-col flex-wrap gap-2">
            <Button variant="primary">
              <Link to={profileUrl}>Claim This Profile</Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-600"
              onClick={() => {
                setShowOptOutModal(true);
              }}
            >
              I don't want to be on the platform
            </Button>
          </div>
          {!isExpanded && (
            <p
              className="cursor-pointer text-sm text-purple-900/60 underline hover:text-purple-900 hover:no-underline"
              onClick={() => setIsExpanded(true)}
            >
              Why and how we created this profile…
            </p>
          )}
        </div>

        <div
          className={cn("flex flex-col gap-4", {
            hidden: !isExpanded,
          })}
        >
          <hr className="my-4 border-purple-200" />
          <CDNImage
            width={120}
            height={120}
            className="self-center rounded-full shadow-sm"
            src="https://form-data.dreamcoachmatch.com/f55530ad-e07a-4799-a5f0-7f36b5429011-Yuri_Minski_1Q9A3830-cut.jpg"
            alt="Yuri Minski"
          />
          <h3 className="text-text-primary text-center font-heading text-xl font-medium">
            Welcome from our Founder & CEO, Yuri Minski
          </h3>

          <div className="text-text-primary prose">
            <p>Hi {coachName},</p>
            <p>
              Welcome to <strong>Dream Coach Match</strong> — a platform created
              to connect exceptional coaches like you with the clients who need
              you most.
            </p>
            <p>
              This profile was created <strong>specifically for you</strong> as
              part of our mission to help <strong>heart-led coaches</strong>{" "}
              attract dream clients and grow their income doing what they love:
              coaching.
            </p>
            <div className="border-l-2 border-purple-200 py-2 pl-4">
              <p className="text-text-secondary text-sm font-medium">
                <Lightbulb className="inline" /> How did we create this draft?
              </p>
              <p className="text-text-tertiary text-sm">
                We used publicly available information — such as your website or
                online bio — and enhanced it with our AI model to suggest what
                you might say.
              </p>
              <p className="text-text-tertiary text-sm">
                No organization (including the International Coaching Federation
                or any other) was involved in providing your data. Everything
                comes from sources already publicly accessible.
              </p>
              <p className="text-text-secondary text-sm font-medium">
                <Sparkles className="inline" /> Your profile is 100% free, and
                you&rsquo;re always in full control.
              </p>
            </div>
            <p>
              Once claimed, you'll unlock tools to promote your services, get
              found online, and connect with aligned clients.
            </p>
            <p className="font-bold">
              Hundreds of coaches have already joined us — and I'd be honored to
              welcome you next.
            </p>
          </div>
          <div className="flex gap-4">
            <div className="flex flex-col justify-start gap-4">
              <p className="text-text-secondary flex flex-col gap-1">
                <span>With deep respect and gratitude,</span>
                <strong>Yuri Minski</strong>
              </p>
              <p className="text-text-tertiary flex flex-col gap-2 text-sm leading-tight">
                <span>Founder & CEO | Dream Coach Match</span>
                <span>
                  Official Business Partner of the International Coaching
                  Federation
                </span>
                <a
                  href="https://www.linkedin.com/in/yuri-minski/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-text-tertiary text-sm underline hover:text-gray-900 hover:no-underline"
                >
                  <LinkedInIcon className="inline size-6" /> Follow me on
                  LinkedIn
                </a>
                <br />
              </p>
            </div>
            <img
              src={SignatureYuriUrl}
              className="h-16 w-auto"
              alt="Signature"
            />
          </div>

          <hr className="my-4 border-purple-200" />

          <IcfPartnerBox />

          <hr className="my-4 border-purple-200" />

          <div className="text-text-secondary flex flex-col items-stretch gap-2 text-sm">
            <a
              href="https://coachingfederation.org/partner/dream-coach-match/"
              target="_blank"
              className="flex items-center gap-2 underline hover:no-underline"
            >
              <ArrowRight />
              View Our Official ICF Partnership
            </a>
            <a
              href="/organizations/icf"
              target="_blank"
              className="flex items-center gap-2 underline hover:no-underline"
            >
              <ArrowRight />
              See ICF Coaches Who Already Joined
            </a>
          </div>

          <hr className="my-4 border-purple-200" />

          <div className="mb-4 flex flex-col items-center gap-2 self-center">
            <h4 className="font-medium">Are you {fullName}?</h4>
            <div className="flex flex-wrap gap-4">
              <Button variant="default" size="sm">
                <Link to={profileUrl}>Claim My Profile</Link>
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setShowOptOutModal(true);
                }}
              >
                Remove me from the platform
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Opt-out Modal */}
      <Modal.Root open={showOptOutModal} onOpenChange={setShowOptOutModal}>
        <Modal.Panel>
          <Modal.Title>Opt Out of Dream Coach Match</Modal.Title>
          <Modal.Description>
            Please complete the form below to request removal from our platform.
          </Modal.Description>
          <div className="mt-4 h-[500px] w-full">
            <iframe
              src="https://api.leadconnectorhq.com/widget/survey/zdZobqi5oGw3AHO3sXLZ"
              className="h-full w-full border-none"
              style={{ border: "none", width: "100%" }}
              scrolling="no"
              id="zdZobqi5oGw3AHO3sXLZ"
              title="Opt-Out Pre-Generated Profile"
            />
            <script src="https://link.msgsndr.com/js/form_embed.js"></script>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              onClick={() => {
                setShowOptOutModal(false);
              }}
            >
              Close
            </Button>
          </div>
        </Modal.Panel>
      </Modal.Root>
    </div>
  );
};
